import React, { useState, useRef } from "react";
import "../assets/styles/Contact.scss";
import emailjs from "@emailjs/browser";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SendIcon from "@mui/icons-material/Send";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import MessageIcon from "@mui/icons-material/Message";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";

function Contact() {
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const [nameError, setNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [messageError, setMessageError] = useState<boolean>(false);

  const [isFeedbackPositive, setIsFeedbackPositive] = useState<boolean | null>(
    null
  );
  const [buttonText, setButtonText] = useState<string>("Send Message");
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);

  const form = useRef<HTMLFormElement>(null);

  // Validate email
  const validateEmail = (email: string): boolean => {
    const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    return regex.test(email);
  };

  // Handle form submission
  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate fields
    const isNameValid = name.length > 1 && name.length < 50;
    const isEmailValid = validateEmail(email);
    const isPhoneValid = phone ? true : false; // Phone is optional
    const isMessageValid = message.length >= 10 && message.length <= 1000;

    setNameError(!isNameValid);
    setEmailError(!isEmailValid);
    setPhoneError(!isPhoneValid);
    setMessageError(!isMessageValid);

    if (isNameValid && isEmailValid && isPhoneValid && isMessageValid) {
      setButtonText("Sending...");

      const templateParams = {
        name,
        email,
        phone,
        message,
      };

      emailjs
        .send(
          "service_f7t67ym", // Replace with your EmailJS service ID
          "template_xn0dio4", // Replace with your EmailJS template ID
          templateParams,
          "wA-K0xj4IObuJ2oZ4" // Replace with your EmailJS public key
        )
        .then(
          (response) => {
            setIsFeedbackPositive(true);
            setButtonText("Sent!");
            setOpenSnackbar(true);
            setName("");
            setEmail("");
            setPhone("");
            setMessage("");
          },
          (error) => {
            setIsFeedbackPositive(false);
            setButtonText("Failed!");
            setOpenSnackbar(true);
          }
        );
    }
  };

  // Close Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <div id="contact">
      <h1>Contact Me</h1>
      <div className="items-container">
        <div className="contact_wrapper">
          <p>
            Got a project waiting to be realized? Let's collaborate and make it
            happen!
          </p>
          <Box
            ref={form}
            component="form"
            noValidate
            autoComplete="off"
            className="contact-form"
            onSubmit={sendEmail}
          >
            <div className="form-flex">
              <div className="input-container">
                <PersonIcon className="input-icon" />
                <TextField
                  required
                  id="outlined-required"
                  label="Your Name"
                  placeholder="What's your name?"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameError(false);
                  }}
                  error={nameError}
                  helperText={
                    nameError ? "Name must be between 2 and 50 characters" : ""
                  }
                  fullWidth
                />
              </div>
              <div className="input-container">
                <EmailIcon className="input-icon" />
                <TextField
                  required
                  id="outlined-required"
                  label="Email"
                  placeholder="How can I reach you?"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(false);
                  }}
                  error={emailError}
                  helperText={
                    emailError ? "Please enter a valid email address" : ""
                  }
                  fullWidth
                />
              </div>
            </div>
            <div className="input-container">
              <PhoneIcon className="input-icon" />
              <PhoneInput
                international
                defaultCountry="KE"
                placeholder="Enter your phone number"
                value={phone}
                onChange={(e: any) => {
                  setPhone(e);
                  setPhoneError(false);
                }}
                className="phone-input"
              />
            </div>
            <div className="input-container">
              <MessageIcon className="input-icon" />
              <TextField
                required
                id="outlined-multiline-static"
                label="Message"
                placeholder="Send me any inquiries or questions"
                multiline
                rows={10}
                className="body-form"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  setMessageError(false);
                }}
                error={messageError}
                helperText={
                  messageError
                    ? "Message must be between 10 and 1000 characters"
                    : ""
                }
                fullWidth
              />
            </div>
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              type="submit"
              className="submit-button"
            >
              {buttonText}
            </Button>
          </Box>
        </div>
      </div>

      {/* Feedback Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={isFeedbackPositive ? "success" : "error"}
        >
          {isFeedbackPositive
            ? "Message received!"
            : "Failed to send message. Please try again."}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Contact;
